import { faEllipsisVertical, faEye, faLock, faLockOpen, faPen, faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge } from '@shared/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu/DropdownMenu';
import type { RenderItemProps } from '@shared/components/ui/VirtualList';
import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { Batch } from '../../../models/Batch';
import { getBatchActions } from '../../../models/batchActions';
import type { BatchesModalsRef } from '../modals/BatchesModals';

const STATUS_STYLES: Record<string, { bg: string; text: string }> = {
  active: { bg: 'bg-bg-success', text: 'text-success' },
  expired: { bg: 'bg-bg-error', text: 'text-error' },
  depleted: { bg: 'bg-card-border light:bg-bg-warning', text: 'text-warning' },
  blocked: { bg: 'bg-card-border light:bg-bg-info', text: 'text-info' },
};

const getBatchStatus = (batch: Batch): string => {
  if (batch.blocked_at && !batch.unblocked_at) return 'blocked';
  if (batch.expiry_date && new Date(batch.expiry_date) < new Date()) return 'expired';
  if (batch.total_stock === 0) return 'depleted';
  return 'active';
};

const formatDate = (date: string | null): string => {
  if (!date) return '\u2014';
  return new Date(date).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

export interface BatchCardProps extends RenderItemProps<Batch> {
  modalsRef: RefObject<BatchesModalsRef | null>;
}

export const Card = ({ item: batch, modalsRef }: BatchCardProps) => {
  const { t } = useTranslation('batchesModule');
  const navigate = useNavigate();
  const status = getBatchStatus(batch);
  const styles = STATUS_STYLES[status] || STATUS_STYLES.active;
  const actions = getBatchActions(batch);
  const stock = batch.total_stock ?? 0;

  return (
    <div className="py-4 border-b border-card-border hover:bg-muted/30 transition-colors">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-title">{batch.batch_number}</span>
          <div className="flex items-center gap-2">
            <Badge className={`text-xs ${styles.bg} ${styles.text}`}>{t(`status.${status}`)}</Badge>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="hover:bg-muted rounded-md p-1.5">
                  <FontAwesomeIcon icon={faEllipsisVertical} className="text-text" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44 border-card-border">
                <DropdownMenuItem asChild>
                  <a
                    href={`/batches/${batch.id}`}
                    className="flex items-center gap-2 no-underline text-inherit w-full"
                    onClick={(e) => {
                      if (!e.ctrlKey && !e.metaKey && e.button === 0) {
                        e.preventDefault();
                        navigate(`/batches/${batch.id}`);
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faEye} className="mr-2 w-3.5" />
                    {t('table.view_detail')}
                  </a>
                </DropdownMenuItem>
                {actions.canEdit && (
                  <DropdownMenuItem onClick={() => modalsRef.current?.openEdit(batch)}>
                    <FontAwesomeIcon icon={faPen} className="mr-2 w-3.5" />
                    {t('actions.edit')}
                  </DropdownMenuItem>
                )}
                {actions.canUnblock && (
                  <DropdownMenuItem onClick={() => modalsRef.current?.openUnblock(batch)}>
                    <FontAwesomeIcon icon={faLockOpen} className="mr-2 w-3.5" />
                    {t('actions.unblock')}
                  </DropdownMenuItem>
                )}
                {actions.canBlock && (
                  <DropdownMenuItem onClick={() => modalsRef.current?.openBlock(batch)}>
                    <FontAwesomeIcon icon={faLock} className="mr-2 w-3.5" />
                    {t('actions.block')}
                  </DropdownMenuItem>
                )}
                {actions.canDelete && (
                  <DropdownMenuItem onClick={() => modalsRef.current?.openDelete(batch)}>
                    <FontAwesomeIcon icon={faTrash} className="mr-2 w-3.5" />
                    {t('actions.delete')}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {batch.supplier_reference && (
          <div className="text-xs text-complementary-text">
            {t('card.supplier_ref')}: {batch.supplier_reference}
          </div>
        )}

        <div className="flex items-center gap-4 text-sm text-subtext">
          <span>
            {t('card.received')}: {formatDate(batch.received_at)}
          </span>
          <span>
            {t('card.expiry')}: {formatDate(batch.expiry_date)}
          </span>
        </div>

        <div className="text-sm text-text font-medium">
          {t('card.stock')}: {stock} uds.
        </div>
      </div>
    </div>
  );
};
